import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Check, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/use-auth';
import { useCurrentOrganization } from '@/hooks/use-organization';
import { useQuickstartAllowed } from '@/hooks/useOnboardingSession';
import {
  QUICKSTART_OPEN_EVENT,
  readQuickstartDismissed,
  readQuickstartSessionAutoOpened,
  useQuickstartProgress,
  writeQuickstartDismissed,
  writeQuickstartInspectVisited,
  writeQuickstartSessionAutoOpened,
  type QuickstartStepId,
} from '@/components/layout/quickstart/useQuickstartProgress';

type StepTitleKey =
  | 'step_connect_tracker_title'
  | 'step_connect_scm_title'
  | 'step1_title'
  | 'step2_title'
  | 'step3_title'
  | 'step4_title'
  | 'step5_title'
  | 'step6_title';

type StepDescKey =
  | 'step_connect_tracker_desc'
  | 'step_connect_scm_desc'
  | 'step1_desc'
  | 'step2_desc'
  | 'step3_desc'
  | 'step4_desc'
  | 'step5_desc'
  | 'step6_desc';

type StepActionKey =
  | 'step_connect_tracker_action'
  | 'step_connect_scm_action'
  | 'step1_action'
  | 'step2_action'
  | 'step3_action'
  | 'step4_action'
  | 'step5_action'
  | 'step6_action';

const STEP_CONFIG: Array<{
  id: QuickstartStepId;
  titleKey: StepTitleKey;
  descKey: StepDescKey;
  actionKey?: StepActionKey;
  actionHref?: string;
  resolveActionHref?: (eligibleForNextShell: boolean) => string;
  onActionClick?: (ctx: { organizationId: string | undefined; userId: string | undefined }) => void;
}> = [
  {
    id: 'connect_issue_tracker',
    titleKey: 'step_connect_tracker_title',
    descKey: 'step_connect_tracker_desc',
    actionKey: 'step_connect_tracker_action',
    actionHref: '/settings/work-items',
  },
  {
    id: 'connect_scm',
    titleKey: 'step_connect_scm_title',
    descKey: 'step_connect_scm_desc',
    actionKey: 'step_connect_scm_action',
    actionHref: '/settings/repositories',
  },
  {
    id: 'start_planning',
    titleKey: 'step1_title',
    descKey: 'step1_desc',
    actionKey: 'step1_action',
    actionHref: '/settings/sdd-generation-gate-policy',
  },
  {
    id: 'approve_artifacts',
    titleKey: 'step2_title',
    descKey: 'step2_desc',
    actionKey: 'step2_action',
    resolveActionHref: (eligibleForNextShell) =>
      eligibleForNextShell ? '/develop/work-items' : '/work-items',
  },
  {
    id: 'connect_mcp',
    titleKey: 'step3_title',
    descKey: 'step3_desc',
    actionKey: 'step3_action',
    actionHref: '/settings/mcp',
  },
  {
    id: 'implement_context',
    titleKey: 'step4_title',
    descKey: 'step4_desc',
    actionKey: 'step4_action',
    resolveActionHref: (eligibleForNextShell) =>
      eligibleForNextShell ? '/develop/work-items/build' : '/work-items',
  },
  {
    id: 'open_pr_review',
    titleKey: 'step5_title',
    descKey: 'step5_desc',
    actionKey: 'step5_action',
    resolveActionHref: (eligibleForNextShell) =>
      eligibleForNextShell ? '/develop/pull-requests' : '/pull-requests',
  },
  {
    id: 'explore_inspect',
    titleKey: 'step6_title',
    descKey: 'step6_desc',
    actionKey: 'step6_action',
    resolveActionHref: (eligibleForNextShell) =>
      eligibleForNextShell ? '/inspect/quality' : '/leaderboard',
    onActionClick: ({ organizationId, userId }) => {
      if (organizationId && userId) {
        writeQuickstartInspectVisited(organizationId, userId, true);
      }
    },
  },
];

function StepStatusIcon({ complete }: { complete: boolean }) {
  return (
    <span
      className={cn(
        'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        complete
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-muted-foreground/35 bg-background text-transparent'
      )}
      aria-hidden
    >
      {complete ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
    </span>
  );
}

export interface GettingStartedHelpProps {
  /** No floating button (the page needs the corner); `QUICKSTART_OPEN_EVENT` still opens the sheet. */
  hideLauncher?: boolean;
  /**
   * No auto-open over this page: the session's auto-open waits for the next page. A sheet that
   * auto-opened before the page said so (its chrome can arrive later) closes again.
   */
  suppressAutoOpen?: boolean;
}

export function GettingStartedHelp({
  hideLauncher = false,
  suppressAutoOpen = false,
}: GettingStartedHelpProps = {}) {
  const { t } = useTranslation('layout');
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [expandedStepId, setExpandedStepId] = useState<QuickstartStepId | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // The open sheet came from the session auto-open (not the button or the open event).
  const autoOpened = useRef(false);

  const userId = useAuth((s) => s.user?.userId);
  const eligibleForNextShell = useAuth(
    (s) => s.user?.nextNavigation?.eligibleForNextShell ?? false
  );
  const { organization } = useCurrentOrganization();
  const organizationId = organization?.organizationId;
  // Organization setup Quickstart is owner-only (DC-479).
  const quickstartAllowed = useQuickstartAllowed();

  const paletteShortcutLabel = useMemo(
    () =>
      typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)
        ? '⌘K'
        : 'Ctrl+K',
    []
  );

  useEffect(() => {
    setDismissed(readQuickstartDismissed(organizationId, userId));
  }, [organizationId, userId]);

  useEffect(() => {
    if (!quickstartAllowed) return;
    if (organizationId && userId && pathname.startsWith('/inspect')) {
      writeQuickstartInspectVisited(organizationId, userId, true);
    }
  }, [quickstartAllowed, organizationId, userId, pathname]);

  useEffect(() => {
    const openQuickstart = () => {
      autoOpened.current = false;
      setDismissed(false);
      if (organizationId && userId) {
        writeQuickstartDismissed(organizationId, userId, false);
      }
      setOpen(true);
    };

    window.addEventListener(QUICKSTART_OPEN_EVENT, openQuickstart);
    return () => window.removeEventListener(QUICKSTART_OPEN_EVENT, openQuickstart);
  }, [organizationId, userId]);

  const progressEnabled = quickstartAllowed && (!dismissed || open);
  const { steps, completedCount, totalCount, allComplete, isLoading } = useQuickstartProgress(
    organizationId,
    userId,
    progressEnabled
  );

  useEffect(() => {
    // With the progress queries starved, `isLoading` is false and
    // `allComplete` is false, so the guards below would not stop a
    // restricted member from auto-opening a sheet they cannot see.
    if (!quickstartAllowed) return;
    if (!organizationId || !userId) return;
    if (suppressAutoOpen) return;
    if (isLoading || allComplete) return;
    if (readQuickstartSessionAutoOpened(organizationId, userId)) return;

    writeQuickstartSessionAutoOpened(organizationId, userId, true);
    autoOpened.current = true;
    setOpen(true);
  }, [quickstartAllowed, organizationId, userId, suppressAutoOpen, isLoading, allComplete]);

  // Once closed, a sheet opened later came from the button or the open event.
  useEffect(() => {
    if (!open) autoOpened.current = false;
  }, [open]);

  // The page asked for no auto-open after it already happened: close it and leave it for later.
  useEffect(() => {
    if (!suppressAutoOpen || !open || !autoOpened.current) return;
    autoOpened.current = false;
    setOpen(false);
    if (organizationId && userId) writeQuickstartSessionAutoOpened(organizationId, userId, false);
  }, [suppressAutoOpen, open, organizationId, userId]);

  const progressById = useMemo(
    () => new Map(steps.map((step) => [step.id, step.complete])),
    [steps]
  );

  const firstIncompleteStepId = useMemo(() => {
    const incomplete = steps.find((step) => !step.complete)?.id;
    return incomplete ?? 'explore_inspect';
  }, [steps]);

  useEffect(() => {
    if (open) {
      setExpandedStepId(firstIncompleteStepId);
    }
  }, [open, firstIncompleteStepId]);

  // Setup Quickstart is owner-only (DC-479); Sorcery has its own chrome.
  if (!quickstartAllowed || pathname.startsWith('/sorcery')) {
    return null;
  }

  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleDismiss = () => {
    if (organizationId && userId) {
      writeQuickstartDismissed(organizationId, userId, true);
    }
    setDismissed(true);
    setOpen(false);
  };

  const handleReopen = () => {
    if (organizationId && userId) {
      writeQuickstartDismissed(organizationId, userId, false);
    }
    setDismissed(false);
    setOpen(true);
  };

  return (
    <>
      {hideLauncher ? null : dismissed ? (
        <Button
          type="button"
          data-testid="getting-started-help-reopen"
          size="icon"
          variant="outline"
          onClick={handleReopen}
          className={cn(
            'pointer-events-auto fixed bottom-5 right-5 z-40 h-9 w-9 rounded-full md:bottom-6 md:right-6',
            'border bg-background shadow-md',
            'transition-[transform,box-shadow] hover:scale-[1.03] hover:shadow-lg active:scale-[0.98]'
          )}
          aria-label={t('gettingStarted.reopen_aria')}
        >
          <Sparkles className="h-[1.1rem] w-[1.1rem]" strokeWidth={2.1} />
        </Button>
      ) : (
        <Button
          type="button"
          data-testid="getting-started-help-fab"
          variant="default"
          onClick={() => setOpen(true)}
          className={cn(
            'pointer-events-auto fixed bottom-5 right-5 z-40 h-10 rounded-full px-4 md:bottom-6 md:right-6',
            'border border-primary/15 bg-primary text-primary-foreground shadow-md',
            'ring-1 ring-primary/20 ring-offset-2 ring-offset-background',
            'transition-[transform,box-shadow] hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]'
          )}
          aria-label={t('gettingStarted.fab_aria')}
        >
          <Sparkles className="mr-2 h-4 w-4" strokeWidth={2.1} />
          <span className="text-sm font-semibold">{t('gettingStarted.fab_label')}</span>
          {!isLoading && completedCount > 0 && !allComplete ? (
            <span className="ml-2 rounded-full bg-primary-foreground/15 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
              {completedCount}/{totalCount}
            </span>
          ) : null}
        </Button>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <div className="border-b px-6 pb-4 pt-6">
            <SheetHeader className="space-y-3 text-left">
              <SheetTitle className="text-xl">{t('gettingStarted.sheet_title')}</SheetTitle>
              <SheetDescription>{t('gettingStarted.sheet_intro')}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t('gettingStarted.progress_label')}</span>
                <span>
                  {isLoading
                    ? t('gettingStarted.progress_loading')
                    : t('gettingStarted.progress_count', {
                        completed: completedCount,
                        total: totalCount,
                      })}
                </span>
              </div>
              <Progress value={progressPercent} aria-label={t('gettingStarted.progress_aria')} />
            </div>
            {eligibleForNextShell ? (
              <p className="mt-4 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {t('gettingStarted.paletteDiscover', { shortcut: paletteShortcutLabel })}
              </p>
            ) : null}
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            <ul className="space-y-2">
              {STEP_CONFIG.map((step) => {
                const complete = progressById.get(step.id) ?? false;
                const expanded = expandedStepId === step.id;
                const panelId = `quickstart-step-panel-${step.id}`;

                return (
                  <li key={step.id}>
                    <div
                      className={cn(
                        'overflow-hidden rounded-lg border bg-card transition-colors',
                        expanded && 'border-primary/40 ring-1 ring-primary/10',
                        complete && !expanded && 'border-border/80'
                      )}
                    >
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 px-3 py-3 text-left"
                        aria-expanded={expanded}
                        aria-controls={panelId}
                        onClick={() => setExpandedStepId(expanded ? null : step.id)}
                      >
                        <StepStatusIcon complete={complete} />
                        <span className="min-w-0 flex-1 pt-0.5">
                          <span className="block text-sm font-semibold text-foreground">
                            {t(`gettingStarted.${step.titleKey}`)}
                          </span>
                          {complete ? (
                            <span className="mt-0.5 block text-xs text-muted-foreground">
                              {t('gettingStarted.step_complete')}
                            </span>
                          ) : null}
                        </span>
                        <ChevronDown
                          className={cn(
                            'mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                            expanded && 'rotate-180'
                          )}
                          aria-hidden
                        />
                      </button>

                      {expanded ? (
                        <div id={panelId} className="border-t px-3 pb-4 pt-1">
                          <p className="pl-9 text-sm leading-relaxed text-muted-foreground">
                            {t(`gettingStarted.${step.descKey}`)}
                          </p>
                          {step.actionKey && (step.actionHref || step.resolveActionHref) ? (
                            <div className="mt-4 pl-9">
                              <Button asChild size="sm" className="rounded-md">
                                <Link
                                  to={
                                    step.resolveActionHref
                                      ? step.resolveActionHref(eligibleForNextShell)
                                      : step.actionHref!
                                  }
                                  onClick={() => {
                                    step.onActionClick?.({ organizationId, userId });
                                    setOpen(false);
                                  }}
                                >
                                  {t(`gettingStarted.${step.actionKey}`)}
                                </Link>
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="border-t px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-center px-0 text-sm text-muted-foreground hover:text-foreground"
              onClick={handleDismiss}
            >
              {t('gettingStarted.dismiss')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
